/**
 * The terminal WebSocket — and the ONLY correct channel for the owner's own keystrokes.
 *
 * Why this matters (the owner caught me getting it wrong): /agent/message is the
 * agent-to-agent bus and frames what it delivers as an incoming agent message. A human
 * typing in the UI is not an agent; their input belongs in the pty, exactly as the
 * original UI does it. Anything else produces unattributed `<agent-msg:…>` blocks in
 * the agent's input.
 *
 * Protocol (verified in wtserver/websocket.py):
 *   - BINARY frames both ways are RAW pty bytes — not base64, not JSON-wrapped.
 *   - TEXT frames are JSON control messages: resize / input / tmux-list /
 *     tmux-exit-copy / capture-scrollback / tmux-rename / tmux-kill / set-name /
 *     claude-watch / claude-unwatch / speak-watch / speak-unwatch.
 *   - A non-JSON TEXT frame falls back to being written to the pty as input.
 *   - Each connection spawns its OWN PtySession (a login shell), so reaching a
 *     particular agent means attaching to its tmux session first.
 *
 * The socket is disposable and the server is the source of truth: tmux already provides
 * session persistence, so a dropped connection is recovered by reconnecting and
 * re-attaching, never by trying to keep this object alive
 * (docs/ARCHITECTURE.md §6.3).
 */

import { onTransportDown, onPty, sendFrame } from '../servers/commandStream'
import { onRelayTargetChange, relayTarget } from './relay'

/**
 * A terminal, over the link the page already holds.
 *
 * The browser cannot open a socket to the machine — no port faces the internet — so
 * `/ws` had nowhere to land. This stands in for it: the SAME three methods the code
 * below already uses, carrying the SAME messages, over `/account/events` instead of a
 * direct connection.
 *
 * Deliberately shaped like a WebSocket rather than replacing the terminal code. The
 * pty, the tmux attach, the resize control message and the copy-mode escape all live
 * above this line and are the ones people have been using; a second terminal written
 * for the cloud path would be a second thing to keep correct.
 *
 * WHAT IT DOES NOT HAVE: an open handshake. Nothing acknowledges `pty.open`, so this
 * reports OPEN as soon as the frame is away and lets the machine's own `pty.closed`
 * be the failure — the same information, one round trip earlier.
 */
class RelaySocket {
  static readonly OPEN = 1
  readyState = 1
  binaryType = 'arraybuffer'
  onopen: (() => void) | null = null
  onmessage: ((ev: MessageEvent<unknown>) => void) | null = null
  onclose: ((ev: CloseEvent) => void) | null = null
  onerror: (() => void) | null = null

  private readonly stream = `t${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`
  private off: (() => void) | null = null
  private offDown: (() => void) | null = null

  constructor(
    private readonly device: string,
    /**
     * Ask the machine to MUTE this stream's output: terminal bytes become
     * throttled {op:'act'} pings. For sockets that exist to carry input and
     * liveness, never to render — Clean View's headless socket. The transcript
     * arrives over HTTP; the byte stream was freight (measured 2717 B/s per
     * busy pane before this existed).
     */
    private readonly quietStream: boolean,
    /**
     * Told when the machine REFUSED rather than dropped.
     *
     * The two look identical from a closed socket and want opposite responses: a
     * drop should reconnect, a refusal must not. Retrying a handshake that will
     * keep answering the same sentence is a loop with an explanation inside it
     * that nobody ever reads — which is exactly what the owner saw, as
     * `disconnected (1000) — reconnecting…` on repeat while the machine was
     * calmly saying "create a username and password to use the terminal".
     */
    private readonly onRefused: (reason: string) => void,
    /** This open carries human intent to hold the live picture (see the open frame). */
    private readonly takeover: boolean,
    /** A plain shell opened beside this agent: see TerminalSocket's `beside`. */
    private readonly beside: string | null = null,
  ) {
    this.off = onPty((frames) => {
      for (const f of frames) {
        if (f.stream !== this.stream) continue
        if (f.op === 'closed') {
          const why = f.reason || 'the terminal closed'
          if (f.refused) this.onRefused(why)
          this.shut(why)
          return
        }
        if (f.op === 'act') {
          // Presented to the layer above as the control frame it morally is —
          // TerminalSocket parses TEXT frames as JSON and dispatches on type,
          // and inventing a second delivery path for one frame would leave two
          // places to keep in step.
          this.onmessage?.({ data: '{"type":"act"}' } as MessageEvent<unknown>)
          continue
        }
        if (typeof f.b64 === 'string') {
          // Bytes, restored as bytes. `onData` takes a Uint8Array and xterm writes
          // it through its own decoder, which is the thing that knows how to hold a
          // half-character until the rest of it arrives.
          const bin = atob(f.b64)
          const bytes = new Uint8Array(bin.length)
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i)
          this.onmessage?.({ data: bytes.buffer } as MessageEvent<unknown>)
        } else if (typeof f.data === 'string') {
          this.onmessage?.({ data: f.data } as MessageEvent<unknown>)
        }
      }
    })
    /*
     * The transport's death is THIS stream's death. The BFF releases every pty
     * when the account socket closes and notifies only the edge — no pty.closed
     * ever reaches the browser, so without this the facade claimed OPEN over a
     * buried stream forever: keystrokes into a void, reconnect code standing
     * down on "ready", recovery only by remount (owner: 需要退出再進來才能變化).
     * Synthesizing the close hands control to the machinery that already knew
     * what to do with one.
     */
    this.offDown = onTransportDown(() => this.shut('the connection to the account service closed'))
    console.debug('[relay-pty] open', this.stream, 'device', this.device)
    /*
     * The open frame goes out AFTER onopen has run, in the same microtask.
     * onopen is where the terminal views synchronously call resize() and
     * attach(session) — so by holding the frame one beat, the session name can
     * ride pty.open itself (the BFF whitelists that field already) and the
     * machine spawns `tmux attach` DIRECTLY, skipping the login-shell profile
     * tax (~300ms on a mac with oh-my-zsh — owner, 2026-08-28: 進入時總是 Lag).
     * Anything sent during that beat queues and flushes right behind the open,
     * so the wire order the machine sees is unchanged: open, then everything.
     */
    queueMicrotask(() => {
      this.onopen?.()
      sendFrame({
        type: 'pty.open',
        stream: this.stream,
        device: this.device,
        // Only ever the literal true — absent means the behaviour every existing
        // stream already has, and the BFF forwards the field on exactly that rule.
        ...(this.quietStream ? { quiet: true } : {}),
        // `attach: true` is the direct-tmux OPT-IN, deliberately separate from
        // `session` (which every legacy build has always sent): the edge spawns
        // tmux as the pty only for clients that declare they handle the
        // {"type":"attached"} ack — a legacy client given a direct pane typed
        // its fallback attach INTO the live session (owner's composer, 8/28).
        ...(this.session
          ? {
              session: this.session,
              attach: true,
              // Single-active-viewer: takeover is INTENT, not visibility — a
              // fresh mount, an overlay tap, a hidden→visible return. An
              // auto-reconnect after a link blip must NOT carry it, or every
              // visible window races and the live view lands on whichever
              // retry happened to arrive last (attack review, 2026-08-28).
              ...(this.takeover ? { takeover: true } : {}),
            }
          : this.beside
            ? // `session` with no `attach`: the machine starts the shell in that
              // agent's folder (edge cloudPty besideTargetOf). The field every
              // build has sent and the BFF forwards, so nothing in between changes.
              { session: this.beside }
            : {}),
      })
      this.openSent = true
      for (const f of this.preOpen.splice(0)) sendFrame(f)
    })
  }

  /** Set by attach() during the onopen beat; false once the frame is away. */
  private session: string | null = null
  private openSent = false
  private preOpen: Record<string, unknown>[] = []

  /** Records the attach target for the open frame. True while that is still possible. */
  setSession(session: string): boolean {
    if (this.openSent) return false
    this.session = session
    return true
  }

  private out(frame: Record<string, unknown>): void {
    if (this.openSent) sendFrame(frame)
    else this.preOpen.push(frame)
  }

  send(data: string | ArrayBufferLike): void {
    if (this.readyState !== RelaySocket.OPEN) return
    const text = typeof data === 'string' ? data : new TextDecoder().decode(data as ArrayBuffer)
    this.out({ type: 'pty.in', stream: this.stream, device: this.device, data: text })
  }

  close(): void {
    if (this.readyState !== RelaySocket.OPEN) return
    this.out({ type: 'pty.close', stream: this.stream, device: this.device })
    this.shut('closed by this page')
  }

  private shut(reason: string): void {
    console.debug('[relay-pty] shut', this.stream, reason)
    this.readyState = 3
    this.off?.()
    this.off = null
    this.offDown?.()
    this.offDown = null
    this.onclose?.(new CloseEvent('close', { code: 1000, reason }))
  }
}

export type TerminalHandlers = {
  /** Raw pty bytes. */
  onData?: (bytes: Uint8Array) => void
  onOpen?: () => void
  onClose?: (ev: CloseEvent) => void
  /**
   * A typed `{"type":"error"}` control frame from the server. This is NOT a transport
   * failure — the upgrade succeeded — so it never reaches onClose with anything
   * distinguishable, and the close that follows looks exactly like a normal one.
   */
  onServerError?: (info: { error: string; host?: string; message?: string }) => void
  /**
   * A new assistant message to read aloud, from the transcript watch.
   *
   * `id` is the JSONL event's uuid, and dedupe MUST key on it rather than on the
   * text: an agent that says the same words in a later turn is a new thing to
   * say, and content-keyed dedupe would silently swallow it.
   *
   * The text arrives already stripped of code fences, inline code, URLs and
   * markdown by the server (_speakable_prose), with NO length cap — splitting
   * and queueing are the client's job.
   */
  onSpeak?: (msg: { id: string; ts: string; text: string }) => void
  /** The watch is live and will push new messages. */
  onSpeakReady?: () => void
  /**
   * The pane WROTE something — the fact, not the bytes. Fires on raw pty bytes
   * (a terminal-view socket, or a quiet stream against an edge too old to mute)
   * and on the act pings that replace them on a quiet stream. This is what the
   * transcript re-read and the busy dot key on; it deliberately carries nothing,
   * because everything it used to carry was thrown away by every subscriber.
   */
  onActivity?: () => void
  /** This session has no Claude transcript, so nothing will ever be pushed. The
   *  old app fell back to scraping the screen; that path is deliberately not
   *  ported, so this simply means "this session does not speak". */
  onSpeakNoFile?: () => void
  /**
   * The pane's current text, rendered server-side by `tmux capture-pane` in answer
   * to a `capture-scrollback` control frame. The live-block feature reads it: the
   * transcript file gains a message only when that message COMPLETES (measured
   * 2026-08-28: the file sat still for up to 35s while the screen kept moving), so
   * the pane is the only place the sentence being written right now exists.
   */
  onScrollback?: (text: string) => void
  /**
   * The message the agent is writing RIGHT NOW, pushed by the machine — the whole
   * text so far, not a delta, and `''` when the turn ends and the real block is
   * about to land. Only CLIs with a live stream of their own send this (codex's
   * app-server today); claude has none, so its live block is read from the pane.
   */
  onLiveText?: (text: string, kind?: string) => void
}

/** Close code the backend uses when the vault locks under a live socket. */
export const CLOSE_VAULT_LOCKED = 4001

export class TerminalSocket {
  private ws: WebSocket | null = null
  private queue: string[] = []
  /**
   * The last typed error the SERVER sent before closing. `host_not_mounted` arrives
   * this way — the upgrade succeeded, so the close event alone carries no cause.
   */
  serverError: string | null = null
  /** Unsubscribe from "a machine was chosen", while there is not one yet. */
  private waiting: (() => void) | null = null
  /** tmux session this pty is attached to — needed to cancel copy mode. */
  private session: string | null = null
  /** When the last typing claim went out — the 1.5s throttle below. */
  private typingClaimAt = 0
  private lastInputAt = 0
  private pendingCopyExit = false

  /** `host` routes the pty to a mounted remote via /ws?host= (remote_proxy_middleware
   *  proxies the upgrade to that edge) — the original's wsURL() does the same. */
  constructor(
    private handlers: TerminalHandlers = {},
    /**
     * The mounted remote this pty belongs to, or null for this machine.
     *
     * KEPT AND UNUSED, on purpose. It was `?host=` on a direct connection and the
     * relay frame has no field for it yet, so a remote session would open on the
     * wrong machine. Dropping the parameter would make the callers stop passing it
     * and lose the information; holding it here keeps the gap visible at the one
     * place that has to close it.
     */
    readonly host: string | null = null,
    /** Mute the stream's output at the machine: see RelaySocket's quietStream. */
    private readonly quietStream = false,
    /** Open on THIS device, ignoring the relay's current target — the sidebar's
     *  per-machine Terminal button (owner, 2026-08-27). Null = follow the relay,
     *  which is what every terminal did before this parameter existed. */
    private readonly device: string | null = null,
    /**
     * A plain shell opened BESIDE this agent (the Cmd+J drawer): the machine starts it
     * in the agent's folder. It used to be typed in — `cd "$(tmux display-message …)"`
     * — which is POSIX, and a Windows machine's PowerShell answered it with a parser
     * error (owner, 2026-10-26). The machine knows its own shell; the page does not.
     * An edge from before this lands the shell where it always started.
     */
    private readonly beside: string | null = null,
  ) {}

  connect(opts: { takeover?: boolean } = {}): void {
    const device = this.device ?? relayTarget()
    if (!device) {
      /*
       * WAIT, do not report.
       *
       * The relay learns which machine to point at from the router, after sign-in
       * and asynchronously; this component mounts and connects immediately. On a
       * fresh load the terminal always got here first.
       *
       * The first version answered it with a close — code 1000, no reason the
       * screen could use — which the layer above read as an ordinary disconnect
       * and retried, once a second, for ever. Every symptom the owner reported
       * came from that: `[disconnected (1000) — reconnecting…]` on a terminal
       * whose stream had never been opened, which is why the machine's log had
       * nothing in it at all.
       *
       * A missing target is not a failure. It is a thing that has not happened
       * yet, and the honest response to that is to wait for it.
       */
      console.debug('[relay-pty] waiting: no machine chosen yet')
      this.waiting?.()
      this.waiting = onRelayTargetChange((id) => {
        if (!id) return
        this.waiting?.()
        this.waiting = null
        this.connect()
      })
      return
    }
    this.waiting?.()
    this.waiting = null
    // `host` — the mounted-remote routing — is not carried yet. It was a query
    // parameter on a direct connection, and the frame has no field for it; naming
    // that here rather than passing it silently, because a remote session opening
    // on the wrong machine is the kind of wrong answer nobody reports.
    const ws = new RelaySocket(device, this.quietStream, (why) => {
      // The machine's own sentence, on the machine's own terms. `serverError` is
      // the field the terminal already reads for "this was refused, not dropped".
      this.serverError = why
      this.handlers.onServerError?.({ error: 'refused', message: why })
    }, opts.takeover === true, this.beside) as unknown as WebSocket
    ws.binaryType = 'arraybuffer'
    this.ws = ws

    ws.onopen = () => {
      console.debug('[term] socket open')
      // Anything typed before the socket opened.
      for (const s of this.queue.splice(0)) this.write(s)
      this.handlers.onOpen?.()
    }

    ws.onmessage = (ev: MessageEvent<unknown>) => {
      if (ev.data instanceof ArrayBuffer) {
        this.handlers.onData?.(new Uint8Array(ev.data))
        // Bytes ARE activity — this is the compatibility half: a quiet stream
        // against an edge that predates muting still flows bytes, and the
        // subscriber above must not care which shape the fact arrived in.
        this.handlers.onActivity?.()
        return
      }
      /*
       * TEXT frames are control messages. Most (name / scrollback / transcript deltas)
       * are consumed by the terminal view, but `error` MUST be handled here.
       *
       * Since avengents-server a114729, connecting with `?host=` to a host with no live
       * mount ACCEPTS the upgrade, sends {"type":"error","error":"host_not_mounted",...}
       * and then closes cleanly — deliberately, so it cannot silently open a local pty
       * in the remote's place. Ignoring the frame turns that into a bare "terminal
       * closed" with no cause, which is the exact failure this app keeps producing:
       * the server said why, and the client threw it away. The server's `message`
       * already tells the user to remount and retry, so it is shown verbatim.
       */
      if (typeof ev.data !== 'string') return
      if (ev.data.includes('"attached"') || ev.data.includes('"attach_fallback"')) {
        try {
          const t = (JSON.parse(ev.data) as { type?: string }).type
          if (t === 'attached') {
            this.attachedDirectly = true
            return
          }
          if (t === 'attach_fallback') {
            // The machine says it did NOT direct-attach (target gone, or a
            // non-direct pty backend): type the attach NOW — event-driven,
            // faster than the old 300ms timer, and never racing a late ack.
            // Unless the target is GONE and the caller holds a plain terminal
            // (`missing`, an edge from 2026-10-18 on): that terminal was closed,
            // and typing its attach would leave a bare shell posing as it.
            if (this.onAttachMissing && (JSON.parse(ev.data) as { missing?: boolean }).missing === true) {
              // Nothing types after this — not the long-stop, not a silence reopen.
              this.attachGone = true
              this.onAttachMissing()
              return
            }
            this.typeAttachNow?.()
            return
          }
        } catch {
          /* fall through to the ordinary parse below */
        }
      }
      try {
        const msg = JSON.parse(ev.data) as {
          type?: string
          error?: string
          host?: string
          message?: string
          id?: string
          ts?: string
          text?: string
          kind?: string
        }
        if (msg.type === 'error' && msg.error) {
          this.serverError = msg.message || msg.error
          this.handlers.onServerError?.({
            error: msg.error,
            ...(msg.host === undefined ? {} : { host: msg.host }),
            ...(msg.message === undefined ? {} : { message: msg.message }),
          })
        } else if (msg.type === 'speak' && msg.text) {
          this.handlers.onSpeak?.({ id: msg.id ?? '', ts: msg.ts ?? '', text: msg.text })
        } else if (msg.type === 'speak-ready') {
          this.handlers.onSpeakReady?.()
        } else if (msg.type === 'speak-nofile') {
          this.handlers.onSpeakNoFile?.()
        } else if (msg.type === 'scrollback') {
          this.handlers.onScrollback?.(msg.text ?? '')
        } else if (msg.type === 'live-text') {
          // `kind` tells the view WHAT this is: agy streams its thinking first and
          // its reply after, and thinking must never be drawn as the reply.
          this.handlers.onLiveText?.(msg.text ?? '', msg.kind)
        } else if (msg.type === 'act' || msg.type === 'claude-touch' || msg.type === 'claude-delta') {
          // Three shapes of one fact. `act`: the pane wrote (quiet stream).
          // `claude-touch`: the transcript FILE changed (signal-only watch, the
          // shape that does not depend on the pane attach having succeeded).
          // `claude-delta`: an edge too old for signal mode answering the same
          // watch with content — the content is ignored, the fact is kept, so
          // the fallback is useful instead of merely tolerated.
          this.handlers.onActivity?.()
        }
      } catch {
        // Not JSON: a control frame we do not model yet. Never tear down on one.
      }
    }

    ws.onclose = (ev) => {
      console.debug('[term] socket closed', ev.code, this.serverError ?? '')
      this.ws = null
      this.handlers.onClose?.(ev)
    }
  }

  get ready(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  /**
   * WHY this socket is not ready, when the reason is a decision rather than a fault.
   *
   * `ready` is false for a link that dropped AND for a seat another window took, and
   * those want opposite answers: one reconnects on its own and the right thing is to
   * wait, the other will never come back until somebody asks for it. Owner, 2026-09-15:
   * 我剛剛嘗試輸入文字給你的 Terminal, 卻卡住, 需要先切換到別的 Agent session 再切回來
   * 才能夠成功將文字信息送出 — the switch away and back was a retake by another name.
   *
   * Set by useTerminalConnection when the edge sends `superseded`, cleared when the
   * view is taken back. `retake` is that hook's own resume, parked here so a caller
   * holding only the socket — App's composer send — can express the same intent the
   * terminal surface already expresses when someone types on a frozen screen.
   */
  frozen = false
  retake: (() => void) | null = null

  /**
   * Write text to the pty as if typed.
   *
   * Sent as a JSON `{type:'input'}` TEXT frame, matching the original exactly. Raw
   * BINARY frames also reach the pty, but going through the same control-message path
   * keeps ordering with tmux-exit-copy well-defined: the server processes ws messages
   * in order and awaits the cancel, so it is guaranteed to land BEFORE this input.
   */
  write(text: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.queue.push(text)
      return
    }
    this.flushCopyExit()
    // Typing IS the claim: every keystroke funnels through here, so the busy lease
    // rides the same socket — see claimTyping for why this transport and not a POST.
    this.claimTyping()
    this.ws.send(JSON.stringify({ type: 'input', data: text }))
  }

  /**
   * Tell the edge this terminal's user is mid-input, so the delivery gate holds
   * agent/chat message injections instead of pasting them into a half-written line
   * (owner: Agent Terminal 模式下,當使用者正在輸入的時候應該也要能夠擋住所有其它
   * Agent msg / chat msg 的傳輸).
   *
   * Over THIS socket, not POST /agent/busy: that route belongs to the edge and this
   * app's BFF proxies only /auth and /account, so the POST 404s and the catch eats
   * it — useBusyHeartbeat documents the gap. The terminal socket is already open to
   * the very edge whose delivery gate reads the claim.
   *
   * The claim is a 4s lease renewed only by further input and lapsing on its own —
   * no release to lose, no timer anywhere. The throttle is plain arithmetic on the
   * next keystroke, so continuous typing sends one frame per 1.5s, comfortably
   * inside the lease, and a pause simply stops renewing.
   */
  claimTyping(): void {
    if (!this.session || !this.ws || this.ws.readyState !== WebSocket.OPEN) return
    const now = Date.now()
    if (now - this.typingClaimAt < 1500) return
    this.typingClaimAt = now
    this.ws.send(JSON.stringify({ type: 'typing', session: this.session }))
  }

  /**
   * Cancel tmux copy mode before input, or the keystroke is EATEN and never reaches
   * the shell — which is exactly why a second Cmd+Enter appeared to do nothing.
   *
   * Scrolling puts tmux into copy mode. The 400ms burst check means continuous typing
   * doesn't re-send this on every keystroke (no per-key latency); it fires at the start
   * of a burst, and a gap between staging text and pressing Enter is a new burst.
   */
  private flushCopyExit(): void {
    const now = performance.now()
    const burstStart = now - this.lastInputAt > 400
    this.lastInputAt = now
    if (!this.pendingCopyExit && !burstStart) return
    this.pendingCopyExit = false
    if (this.session && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'tmux-exit-copy', session: this.session }))
    }
  }

  /**
   * Call ONLY for a deliberate user scroll gesture (scrollbar drag, touch drag): the
   * next input then cancels tmux copy mode first.
   *
   * NEVER wire this to xterm's `onScroll` — that fires for programmatic scrolls caused
   * by incoming output, and the resulting tmux-exit-copy forces a redraw, which is
   * itself output, which scrolls again: a self-sustaining jump loop. The original only
   * sets this flag from gestures.
   */
  markScrolled(): void {
    this.pendingCopyExit = true
  }

  /** Structured control message. */
  control(msg: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg))
  }

  /**
   * Attach (or clear) the spoken-feed handlers after construction.
   *
   * The socket is created by the agent view, but the speaker is App's concern —
   * it owns the voice settings and the TTS queue. Rather than thread a callback
   * through the view that does not care about it, App patches these three in
   * when it starts watching and clears them when it stops. Passing `{}` clears,
   * so a stopped watch cannot leave a stale closure speaking.
   */
  setSpeakHandlers(
    h: Pick<TerminalHandlers, 'onSpeak' | 'onSpeakReady' | 'onSpeakNoFile'>,
  ): void {
    // Rebuild rather than assign: under exactOptionalPropertyTypes an absent key
    // and a key set to undefined are different types, and clearing must actually
    // REMOVE the handler so no stale closure can speak after the watch stops.
    const { onSpeak: _a, onSpeakReady: _b, onSpeakNoFile: _c, ...rest } = this.handlers
    this.handlers = {
      ...rest,
      ...(h.onSpeak ? { onSpeak: h.onSpeak } : {}),
      ...(h.onSpeakReady ? { onSpeakReady: h.onSpeakReady } : {}),
      ...(h.onSpeakNoFile ? { onSpeakNoFile: h.onSpeakNoFile } : {}),
    }
  }

  resize(rows: number, cols: number): void {
    this.control({ type: 'resize', rows, cols })
  }

  /**
   * Ask the machine to repaint this pane via `tmux refresh-client` — a
   * CLIENT-PRIVATE full redraw with NO geometry change. The safe-refresh
   * mechanisms briefly rode a cols±1 resize nudge instead, and under tmux's
   * `window-size latest` every nudge flipped the shared window's geometry for
   * EVERY client of the session — the owner's phone returning to the
   * foreground made the Mac's pane jump mid-typing (2026-08-28, 亂跳).
   * Edges that predate the op ignore it (`default: break`), so this degrades
   * to no refresh, never to a typed line or a resize.
   */
  refreshView(): void {
    this.control({ type: 'refresh' })
  }

  /**
   * Attach this pty to an existing tmux session.
   *
   * Deliberately WITHOUT `-d`. Detaching other clients kicks the old UI off the
   * session — visible as `[detached (from session …)]` in its pane — which is actively
   * disruptive while both UIs are in use. Sharing means tmux sizes the session to the
   * smallest attached client, which is merely cosmetic. A cosmetic problem beats
   * stealing someone's session.
   */
  attach(session: string, opts: { onSilence?: () => void; onMissing?: () => void } = {}): void {
    // Remember it BEFORE writing: flushCopyExit needs the session name, and the attach
    // itself is the first input on this socket.
    this.session = session
    this.onAttachMissing = opts.onMissing ?? null
    /*
     * DIRECT FIRST (2026-08-28): during the onopen beat the session can still ride
     * the pty.open frame, and a machine that understands it spawns `tmux attach`
     * as the pty — no login shell, no profile tax, no settle wait. The machine
     * answers {"type":"attached"}; if that ack does not arrive within the same
     * 300ms the typed path always waited, the old behaviour engages verbatim —
     * old edges, unknown sessions and fallback pty backends all land there.
     */
    const direct = (this.ws as unknown as { setSession?: (s: string) => boolean })?.setSession?.(session) === true
    /*
     * EVENT-DRIVEN, NOT A RACE (2026-08-28, second composer sighting): the typed
     * fallback used to fire on a flat 300ms timer racing the ack, and a relay
     * tail longer than the timer typed `tmux attach …` INTO a pane that WAS
     * direct — the input gate then delivered it straight into the agent's
     * composer. A machine that understood the request now answers one of TWO
     * frames: {"type":"attached"} → never type; {"type":"attach_fallback"}
     * (target missing / non-direct backend) → type NOW, faster than the timer
     * ever was. The timer survives only as the long-stop for OLD edges that
     * answer neither — 1500ms, because a machine that never says anything is
     * pre-opt-in and its login shell needs the typed attach eventually.
     */
    this.typeAttachNow = () => {
      if (this.typedAttach) return
      this.typedAttach = true
      if (this.ws?.readyState !== WebSocket.OPEN) return
      console.debug('[term] attach →', session)
      this.write(`tmux attach -t ${shellQuote(session)} 2>/dev/null\n`)
    }
    window.setTimeout(() => {
      if (this.attachGone) return
      if (direct && this.attachedDirectly) return
      /*
       * RETAKE OPENS NEVER TYPE (owner's composer polluted a fourth time,
       * 2026-08-28, under rapid handover: an ack that missed its 1500ms window
       * made the long-stop type `tmux attach …` into a pane that WAS attached
       * — straight into the agent's composer). A retake only ever follows a
       * received `superseded`, which PROVES the edge is modern and will answer
       * attached/attach_fallback — so on silence the caller reopens instead.
       * The typed long-stop survives solely for cold opens against old edges.
       */
      if (opts.onSilence) {
        opts.onSilence()
        return
      }
      this.typeAttachNow?.()
    }, direct ? 1500 : 300)
  }

  /** The {"type":"attached"} ack landed — the pty IS the tmux client already. */
  private attachedDirectly = false
  /** A plain terminal's "its session is gone" (attach's onMissing) — never a typed attach. */
  private onAttachMissing: (() => void) | null = null
  /** The machine said the plain terminal is gone; this socket types no attach after it. */
  private attachGone = false
  /** The typed fallback, armed by attach(); fired at most once — by the
   *  machine's attach_fallback frame or by the long-stop timer. */
  private typeAttachNow: (() => void) | null = null
  private typedAttach = false

  close(): void {
    this.waiting?.()
    this.waiting = null
    this.ws?.close()
    this.ws = null
  }
}

/** Single-quote for POSIX shells; the only metacharacter that survives is `'`. */
function shellQuote(s: string): string {
  return `'${s.replaceAll("'", `'\\''`)}'`
}
